// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — browser-authoritative batchHash (ABB-S4, INV-HASH)
// ═══════════════════════════════════════════════════════════════════════════
//
// The browser computes a STABLE RFC-8785 (JCS) SHA-256 over the CANONICAL OPERATION
// IDENTITY — the canonical PARSED batch PLUS the optimistic-concurrency preconditions
// — and returns it on the `rpc-result` / `rpc-error` terminal. This is the
// AUTHORITATIVE replay identity the companion adopts on terminal for the durable
// ledger ([N1]; `correlation.ts:127` `terminal.batchHash`) — the companion's own
// provisional `computeEnvelopeHash` is REPLACED by this wire value on terminal,
// so a missing/empty hash → a different replay identity (INV-HASH).
//
// SCOPE — the FULL OPERATION IDENTITY, NOT the batch alone (round-2 P1 fix). The
// durable outcomeId is keyed by `{bootId, sessionId, sessionEpoch, correlationId,
// batchHash}` (`outcomeLedger.ts:142-150`) and the ledger DE-DUPES an already-seen
// outcomeId (`outcomeLedger.ts:527-530`). The companion's PROVISIONAL fallback hash
// (`correlation.ts computeEnvelopeHash`, used by `wsServer.ts:1104` when the browser
// omits its hash) covers the FULL forwarded payload — `batch` + every precondition
// (`base{Snapshot,Geometry,Table,Context}Revision`, `manifestVersion`, `force`,
// `label`; `commandsRoute.ts:534 buildOpaqueRpcPayload` = the body MINUS the
// transport fields). Both the browser hash AND that fallback feed the SAME
// `OutcomeIdentity.batchHash`, so the browser hash MUST cover the SAME identity set,
// else the two sources disagree on identity:
//
//   • a correlationId REUSED after the replay TTL with the SAME parsed batch but a
//     DIFFERENT precondition (a corrected `baseSnapshotRevision`, or `force` flipped)
//     is a DISTINCT operation. If the browser hashed ONLY the parsed batch (round-1),
//     both turns emit the SAME batchHash → the ledger mints the SAME outcomeId →
//     the second (corrected) terminal is treated as a replay of the first → the
//     CORRECTED outcome is LOST (the round-2 P1). Hashing the preconditions too makes
//     the two operations hash DIFFERENTLY → two distinct ledger entries (correct).
//
//   • round-1's PARSE-EQUAL property is PRESERVED: the BATCH component is the canonical
//     PARSED batch (Zod-defaulted), so a defaulted-field-omitted retry (same parsed
//     form, same preconditions) still hashes identically (an idempotent retry).
//
// `label` IS part of the identity (the companion's provisional `computeEnvelopeHash`
// covers it, `commandsRoute.ts:304/532`). The two hash sources feed one identity
// field, so excluding `label` here would make a label-differing pair COLLAPSE when the
// browser hash is present but stay DISTINCT under the provisional fallback — an
// asymmetry. The contract (the companion) is the source of truth (ПРИОРИТЕТ #1), so
// `label` is included to keep the two sources identity-consistent.
//
// `correlationId` is EXCLUDED — it is the OUTER key in the `OutcomeIdentity` tuple,
// not part of the hashed identity (hashing it would make every distinct correlationId
// hash differently, defeating the cross-id payload-collision protection the fallback
// gives).
//
// CANONICALIZATION — SHARED, one truth (ПРИОРИТЕТ #2 + Этап-1 DRY). The ONE
// RFC-8785 (JCS) algorithm lives in the LEAF package `@dxf-ai-editor/ai-commands/
// canonical-json` (`canonicalizeJson`), which BOTH this browser batchHash AND the
// companion's `apps/node-companion/src/canonicalJson.ts` (→ correlation.ts
// computeEnvelopeHash + cursorKeyEnvelope.ts) import — so the two sides cannot drift.
// A cross-app import (apps/web → apps/node-companion) is forbidden, so the algorithm
// is HOISTED to the shared leaf rather than copied. This module applies the BATCHHASH
// policy (reject non-finite + non-JSON-representable, NFC-normalize strings + keys)
// over that one traversal. `commandRun.test.ts` pins the exact wire form + the
// per-precondition SENSITIVITY so a drift / a dropped precondition surfaces as a
// failing test, not a silent identity collision.
//
// ABSENT / DEFAULT PRECONDITIONS canonicalize STABLY: an `undefined`-valued key is
// OMITTED by `canonicalizeJson` (canonicalJson.ts:82-84), so an ABSENT precondition
// (omitted from the payload OR explicitly `undefined`) always hashes the same — two
// identical absent-precondition turns match. `force` is NORMALIZED to a stable boolean
// (`force === true`) so an ABSENT `force` and `force:false` collapse to the SAME hash
// (the same "not forced" operation), while `force:true` is distinct.
//
// LEAF module: imports ONLY `@dxf-ai-editor/ai-commands/canonical-json` (an ALLOWED
// direction) + Web Crypto SHA-256. No socket, no editorStore, no node-companion.
// ═══════════════════════════════════════════════════════════════════════════

import { canonicalizeJson } from "@dxf-ai-editor/ai-commands/canonical-json";

/**
 * Thrown when the canonical identity contains a value that is NOT JSON-representable
 * (NaN / ±Infinity, or an `undefined` / function / symbol / bigint) — the [N1.d]
 * fail-closed posture: an operation identity must never hash a non-finite /
 * non-serializable value (it would not survive `JSON.stringify` to the wire either).
 * The handler maps this to `ai-command-rejected` 422 `non-serializable-batch`
 * (mirroring the companion's own pre-forward `assertJsonRepresentable` reject).
 */
export class NonSerializableBatchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NonSerializableBatchError";
  }
}

/**
 * The optimistic-concurrency preconditions that, together with the parsed batch,
 * define a DISTINCT operation (round-2 P1). EXACTLY the precondition fields the
 * companion's provisional `computeEnvelopeHash` covers (minus `correlationId`, the
 * outer ledger key, and minus `batch`, supplied separately as the parsed form). Every
 * field is OPTIONAL — an absent precondition canonicalizes STABLY (the key is omitted).
 *
 * `force` is the agent's RAW forwarded value (`unknown`) — the builder NORMALIZES it to
 * a stable boolean (`force === true`) so absent-vs-false collapse to one identity.
 */
export interface BatchHashPreconditions {
  readonly baseSnapshotRevision?: unknown;
  readonly baseGeometryRevision?: unknown;
  readonly baseTableRevision?: unknown;
  readonly baseContextRevision?: unknown;
  readonly manifestVersion?: unknown;
  readonly force?: unknown;
  readonly label?: unknown;
}

/**
 * The ordered list of precondition fields the batchHash is SENSITIVE to (excluding the
 * batch itself). The by-construction sensitivity test in `commandRun.test.ts` iterates
 * THIS list so a FUTURE-added precondition field omitted from the hash fails the test
 * (by-construction completeness — the lesson encoded as a gate).
 */
export const BATCH_HASH_PRECONDITION_FIELDS = [
  "baseSnapshotRevision",
  "baseGeometryRevision",
  "baseTableRevision",
  "baseContextRevision",
  "manifestVersion",
  "force",
  "label",
] as const;

/**
 * The canonical operation-identity object the batchHash is computed over — the parsed
 * batch PLUS the preconditions. Exported so tests build the identity the same way the
 * handler does (one truth for the canonical input shape).
 */
export interface BatchHashIdentity extends BatchHashPreconditions {
  readonly batch: unknown;
}

/**
 * Build the canonical operation-identity object from the parsed batch + the raw
 * forwarded preconditions, NORMALIZING `force` to a stable boolean. Absent
 * preconditions are left `undefined` (the canonicalizer omits the key → stable). One
 * truth for both the handler call site AND the sensitivity test, so they cannot drift.
 */
export function buildBatchHashIdentity(
  parsedBatch: unknown,
  preconditions: BatchHashPreconditions,
): BatchHashIdentity {
  return {
    batch: parsedBatch,
    baseSnapshotRevision: preconditions.baseSnapshotRevision,
    baseGeometryRevision: preconditions.baseGeometryRevision,
    baseTableRevision: preconditions.baseTableRevision,
    baseContextRevision: preconditions.baseContextRevision,
    manifestVersion: preconditions.manifestVersion,
    // NORMALIZE: absent / false / any-non-true → false; exactly true → true. So an
    // ABSENT `force` and `force:false` collapse to the SAME identity ("not forced"),
    // while `force:true` is distinct (the force-sensitivity guard).
    force: preconditions.force === true,
    label: preconditions.label,
  };
}

/**
 * RFC-8785 (JCS) canonical serialization with the BATCHHASH policy — the SHARED
 * `canonicalizeJson` with `{onNonFinite:throw, onNonJson:throw, nfcStrings:true}`
 * (reject a non-finite / non-JSON-representable value fail-closed [N1.d];
 * NFC-normalize strings + keys [N1.e]). Identical to the companion's batchHash
 * policy (`correlation.ts canonicalize`), so the two sides hash a logical value to
 * the SAME string.
 */
export function canonicalizeBatchJson(value: unknown): string {
  return canonicalizeJson(value, {
    onNonFinite: () => {
      throw new NonSerializableBatchError(
        "batch identity contains a non-finite number (NaN/Infinity) — non-serializable",
      );
    },
    onNonJson: (typeofValue) => {
      throw new NonSerializableBatchError(
        `batch identity contains a non-serializable value of type "${typeofValue}"`,
      );
    },
    nfcStrings: true,
  });
}

/** Lower-case hex of a byte buffer (the wire form the companion stores verbatim). */
function toHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let hex = "";
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

/**
 * Compute the browser-authoritative `batchHash`: the lower-case hex SHA-256 of the
 * RFC-8785 (JCS) canonicalization of the canonical OPERATION IDENTITY (the parsed
 * batch + the optimistic-concurrency preconditions). Async because it uses Web Crypto
 * `crypto.subtle.digest` (available in the browser page + jsdom); the rpc handler is
 * async, so awaiting it costs nothing on the hot path.
 *
 * Pass the parsed batch + the raw forwarded preconditions; the identity is built
 * (`buildBatchHashIdentity`) so absent preconditions canonicalize stably and `force`
 * is normalized to a stable boolean.
 *
 * THROWS {@link NonSerializableBatchError} if the identity carries a non-finite /
 * non-serializable value (the handler maps it to a 422 reject) — fail-closed, so
 * the wire never carries a hash over a value the companion's own pre-forward
 * `assertJsonRepresentable` would already have rejected.
 */
export async function computeBatchHash(
  parsedBatch: unknown,
  preconditions: BatchHashPreconditions = {},
): Promise<string> {
  const identity = buildBatchHashIdentity(parsedBatch, preconditions);
  const canonical = canonicalizeBatchJson(identity);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonical),
  );
  return toHex(digest);
}
